import React, { useRef, useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import WritePostDrawer from '@/components/WritePostDrawer';
import IdeaCards from '@/components/chat/IdeaCards';
import PostDraftCard from '@/components/chat/PostDraftCard';
import ContentEditChat from '@/components/chat/ContentEditChat';
import { ChatImageUpload } from '@/components/chat/ChatImageUpload';
import ReactMarkdown from 'react-markdown';
import { Send, Plus, Mic } from 'lucide-react';
import type { CalendarItem } from '@/pages/PlannedContent';
import type { PostIdea } from '@/components/chat/IdeaCards';
import { parseChatMessage, renderCardContent } from '@/utils/chatCardParsingHelpers';
import { supabase } from '@/integrations/supabase/client';
import { useAppState } from '@/context/AppStateContext';
import { useParams } from 'react-router-dom';
import { DEFAULT_SECTION_TYPES } from '@/config/sectionDefaults';
import { useChatImageUpload } from '@/hooks/useChatImageUpload';

type ChatMessage = {
  role: 'assistant' | 'user';
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _magic?: string;
  _loading?: boolean;
  _mode?: 'unknown' | 'chat' | 'idea' | 'write';
};

interface MainAppChatProps {
  messages: ChatMessage[];
  input: string;
  setInput: (val: string) => void;
  sending: boolean;
  onSend: () => void;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  calendarItems: CalendarItem[];
  setCalendarItems: React.Dispatch<React.SetStateAction<CalendarItem[]>>;
  writeDrawerOpen: boolean;
  setWriteDrawerOpen: (open: boolean) => void;
  writeDrawerData: { id?: string; subject_matter: string; post_content: string; post_digest?: string } | null;
  setWriteDrawerData: React.Dispatch<React.SetStateAction<{ id?: string; subject_matter: string; post_content: string; post_digest?: string } | null>>;
  handleCreateOutline: (idea: PostIdea) => void;
  generateDigest: (content: string, maxLength?: number) => string;
  scrollRef: React.RefObject<HTMLDivElement>;
  currentWebsite?: { id: string; url: string };
  // Content editing props
  isEditingPost?: boolean;
  editingPostData?: {
    id: string;
    title: string;
    content: string;
  } | null;
  onContentUpdate?: (newContent: string) => void;
  onCloseEditChat?: () => void;
}

export default function MainAppChat({
  messages,
  input,
  setInput,
  sending,
  onSend,
  drawerOpen,
  setDrawerOpen,
  calendarItems,
  setCalendarItems,
  writeDrawerOpen,
  setWriteDrawerOpen,
  writeDrawerData,
  setWriteDrawerData,
  handleCreateOutline,
  generateDigest,
  scrollRef,
  currentWebsite,
  isEditingPost = false,
  editingPostData = null,
  onContentUpdate,
  onCloseEditChat,
}: MainAppChatProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { setCurrentRightPaneUrl } = useAppState();
  
  // Image upload management
  const { selectedImage, handleImageSelect, handleImageRemove, uploadImageToStorage, isUploading } = useChatImageUpload();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  // Focus input after sending message
  useEffect(() => {
    if (!sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sending]);


  // If we're editing a post, show the content edit chat
  if (isEditingPost && editingPostData && onContentUpdate && onCloseEditChat) {
    
    return (
      <ContentEditChat
        postThemeId={editingPostData.id}
        postTitle={editingPostData.title}
        postContent={editingPostData.content}
        onContentUpdate={onContentUpdate}
        onClose={onCloseEditChat}
        postType={editingPostData.postType}
        platform={editingPostData.platform}
      />
    );
  }
  // Avoid per-render log spam when not editing

  return (
    <div className="flex h-full w-full flex-col">
      {/* Chat messages */}
      <ScrollArea ref={scrollRef as any} className="flex-1 px-4">
        <div className="space-y-4 pb-4 pt-4">
          {messages.map((m, idx) => {
            // Use card parsing helpers to detect and render cards
            try {
              const parsed = parseChatMessage(m.content);
              if (parsed) {
                return (
                  <div key={idx} className="flex justify-start">
                    <div className="relative">
                      {/* Magic sparkles background */}
                      <div className="absolute -inset-4 opacity-0 animate-sparkle-reveal">
                        <div className="absolute top-0 left-0 text-yellow-400 animate-sparkle" style={{ animationDelay: '0s' }}>✨</div>
                        <div className="absolute top-2 right-2 text-yellow-300 animate-sparkle" style={{ animationDelay: '0.3s' }}>⭐</div>
                        <div className="absolute bottom-1 left-3 text-yellow-400 animate-sparkle" style={{ animationDelay: '0.6s' }}>✨</div>
                        <div className="absolute bottom-0 right-0 text-yellow-300 animate-sparkle" style={{ animationDelay: '0.9s' }}>⭐</div>
                      </div>
                      {/* Card with magical entrance */}
                      <div className="relative w-full max-w-2xl animate-magic-reveal">
                        {parsed.type === 'ideas' ? (
                          <IdeaCards 
                            ideas={parsed.data} 
                            onCreateOutline={handleCreateOutline}
                          />
                        ) : parsed.type === 'post_draft' ? (
                          <PostDraftCard
                            title={parsed.data.title}
                            intro={parsed.data.digest || ''}
                            buttonLabel={(calendarItems.find((it) => (it.title || '').trim().toLowerCase() === (parsed.data.title || '').trim().toLowerCase()) ? 'Go to post' : undefined)}
                            onAddPlanned={async () => {
                              console.log('MainAppChat: Write about this topic button clicked!', {
                                title: parsed.data.title,
                                currentWebsite: currentWebsite,
                                calendarItemsCount: calendarItems.length
                              });
                              // If an item with the same title already exists, navigate to that post instead
                              const existingItem = calendarItems.find((it) => (it.title || '').trim().toLowerCase() === (parsed.data.title || '').trim().toLowerCase());
                              if (existingItem) {
                                console.log('MainAppChat: Existing post found, navigating to editor', { id: existingItem.id });
                                setCurrentRightPaneUrl(`/content-editor/${existingItem.id}`);
                                return;
                              }
                              
                              console.log('MainAppChat: No duplicate found, proceeding with generation');

                              // Generate the full article first
                              const itemId = crypto.randomUUID();
                              const tempItem: CalendarItem = {
                                id: itemId,
                                title: parsed.data.title,
                                digest: parsed.data.digest,
                                isGenerating: true,
                                scheduled_at: new Date().toISOString()
                              };

                              // Add temp item to calendar with loading state (don't open drawer)
                              setCalendarItems((prev) => {
                                const next = [tempItem, ...prev];
                                localStorage.setItem('calendar_items', JSON.stringify(next));
                                localStorage.setItem('calendar_enabled', '1');
                                window.dispatchEvent(new Event('calendar-enabled-changed'));
                                return next;
                              });

                              // Insert into database immediately with generating status (before navigating to avoid fetch race)
                              console.log('MainAppChat: Inserting post theme with generating status');
                              const { data: initialPostTheme, error: initialError } = await supabase
                                .from('post_themes')
                                .insert({
                                  id: itemId, // Use the same UUID for consistency
                                  website_id: currentWebsite?.id,
                                  subject_matter: parsed.data.title,
                                  keywords: parsed.data.keywords || [],
                                  status: 'generatingidea', // Use the generating status
                                  post_digest: parsed.data.digest || '',
                                  content_type: parsed.data.content_type || 'post',
                                  format_template: parsed.data.format_template || 'guide',
                                  length_tag: parsed.data.length_tag || 'long',
                                  tone_style: parsed.data.tone_style || {},
                                  section_layout: parsed.data.section_layout || [],
                                  element_settings: parsed.data.element_settings || {},
                                  scheduled_date: new Date().toISOString(),
                                  original_user_prompt: parsed.originalUserPrompt
                                })
                                .select()
                                .single();

                              if (initialError) {
                                console.error('MainAppChat: Error creating initial post theme:', initialError);
                              } else {
                                console.log('MainAppChat: Successfully created initial post theme with generating status:', initialPostTheme);
                              }

                              // Defer navigation until tags/sections are saved to avoid stale meta in editor
                              console.log('MainAppChat: Deferring navigation until AI meta is saved');

                              try {
                                // Ensure user has website access before proceeding
                                if (currentWebsite?.id) {
                                  const { data: { user } } = await supabase.auth.getUser();
                                  if (user) {
                                    // Check if user has website access
                                    const { data: websiteAccess, error: accessError } = await supabase
                                      .from('website_access')
                                      .select('id')
                                      .eq('user_id', user.id)
                                      .eq('website_id', currentWebsite.id)
                                      .single();

                                    if (accessError && accessError.code === 'PGRST116') {
                                      // No access record found, create one
                                      console.log('MainAppChat: Creating website access record for user');
                                      const { error: createAccessError } = await supabase
                                        .from('website_access')
                                        .insert({
                                          user_id: user.id,
                                          website_id: currentWebsite.id,
                                          access_status: 'confirmed'
                                        });

                                      if (createAccessError) {
                                        console.error('MainAppChat: Failed to create website access:', createAccessError);
                                      } else {
                                        console.log('MainAppChat: Website access record created successfully');
                                      }
                                    } else if (accessError) {
                                      console.error('MainAppChat: Error checking website access:', accessError);
                                    } else {
                                      console.log('MainAppChat: User has website access');
                                    }
                                  }
                                }

                                // Initialize variables with fallback values
                                let format_template = parsed.data.format_template || 'guide';
                                let length_tag = parsed.data.length_tag || 'medium';
                                let tone_style = 'professional';
                                let suggestedKeywords = parsed.data.keywords || [];
                                let section_layout: string[] = Array.isArray(parsed.data.section_layout) ? parsed.data.section_layout : [];

                                // Step 1: Get AI suggestions for format, length, tone and keywords (with fallback)
                                console.log('MainAppChat: Step 1 - Getting AI suggestions for post format');
                                console.log('MainAppChat: Request URL:', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/suggest-post-format`);
                                
                                // Step 1: Get AI suggestions and update database
                                try {
                                  const suggestResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/suggest-post-format`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                                      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                                    },
                                    body: JSON.stringify({
                                      title: parsed.data.title,
                                      website_url: currentWebsite?.url || localStorage.getItem('onboardingWebsite') || '',
                                      website_id: currentWebsite?.id,
                                      language: 'en',
                                      keywords: parsed.data.keywords || []
                                    })
                                  });

                                  console.log('MainAppChat: Suggest response status:', suggestResponse.status);
                                  if (suggestResponse.ok) {
                                    const suggestData = await suggestResponse.json();
                                    console.log('MainAppChat: AI suggestions received:', suggestData);

                                    if (suggestData.success && suggestData.suggestion) {
                                      const suggestion = suggestData.suggestion;
                                      format_template = suggestion.format_template;
                                      length_tag = suggestion.length_tag;
                                      tone_style = suggestion.tone_style;
                                      suggestedKeywords = suggestion.keywords || suggestedKeywords;
                                      const suggestedCategories = suggestion.categories || [];

                                      // Update the database record with AI suggestions
                                      console.log('MainAppChat: Updating post theme with AI suggestions');
                                      const { data: updatedPostTheme, error: updateError } = await supabase
                                        .from('post_themes')
                                        .update({
                                          format_template,
                                          length_tag,
                                          tone_style: { tone: tone_style },
                                          keywords: suggestedKeywords
                                        })
                                        .eq('id', itemId)
                                        .select()
                                        .single();

                                      if (updateError) {
                                        console.error('MainAppChat: Error updating post theme with suggestions:', updateError);
                                        throw new Error(`Failed to update post theme with AI suggestions: ${updateError.message}`);
                                      }

                                      console.log('MainAppChat: Successfully updated post theme with AI suggestions:', updatedPostTheme);

                                      // Save category associations (using the same pattern as generate-post-ideas)
                                      if (suggestedCategories.length > 0) {
                                        console.log('MainAppChat: Saving category associations:', suggestedCategories);
                                        
                                        // Insert categories one by one to avoid batch errors
                                        for (const categoryId of suggestedCategories) {
                                          const { error: categoryError } = await supabase
                                            .from('post_theme_categories')
                                            .insert({
                                              post_theme_id: itemId,
                                              wordpress_category_id: categoryId
                                            });
                                          
                                          if (categoryError) {
                                            console.error(`MainAppChat: Error creating category association for ${categoryId}:`, categoryError);
                                          } else {
                                            console.log(`MainAppChat: Successfully associated category ${categoryId} with post theme ${itemId}`);
                                          }
                                        }
                                      } else {
                                        console.log('MainAppChat: No categories to associate with post theme');
                                      }
                                    } else {
                                      console.warn('MainAppChat: AI suggestions failed, using fallback values');
                                    }
                                  } else {
                                    const errorText = await suggestResponse.text();
                                    console.warn('MainAppChat: Suggest function failed, using fallback values:', errorText);
                                  }
                                } catch (error) {
                                  console.warn('MainAppChat: Error getting AI suggestions, using fallback values:', error);
                                }

                              // Step 1.5: Ensure sections are set before generation (use frontend defaults to avoid RLS)
                              try {
                                const excluded = new Set(['quote', 'image', 'testimonial']);
                                const validated = DEFAULT_SECTION_TYPES
                                  .map((s: any) => s.id)
                                  .filter((id: string) => !excluded.has(id));
                                if (validated.length > 0) {
                                  section_layout = validated;
                                }
                                if (section_layout.length > 0) {
                                  const { error: updateSectionsError } = await supabase
                                    .from('post_themes')
                                    .update({ section_layout })
                                    .eq('id', itemId);
                                  if (updateSectionsError) {
                                    console.warn('MainAppChat: Failed to set section_layout on post_themes', updateSectionsError);
                                  }
                                }
                              } catch (e) {
                                console.warn('MainAppChat: Error ensuring section layout prior to generation', e);
                              }

                                // Step 2: Generate content only after AI suggestions and sections are processed

                                // Navigate now that meta is ready
                                console.log('MainAppChat: Navigating to ContentEditor with prepared meta', { itemId });
                                setCurrentRightPaneUrl(`/content-editor/${itemId}`);

                                // Editor will initiate SSE streaming directly; stop here
                                console.log('MainAppChat: Skipping SSE in chat; editor will stream directly');
                                return;
                              } catch (error) {
                                console.error('Error generating article:', error);
                                
                                // Do not mark as declined here; v4 may be streaming to DB
                                
                                // Keep the item but mark it as failed (don't remove it)
                                setCalendarItems((prev) => {
                                  const updated = prev.map(item =>
                                    item.id === itemId
                                      ? {
                                          ...item,
                                          isGenerating: false,
                                          isGenerated: false,
                                          fullContent: undefined
                                        }
                                      : item
                                  );
                                  localStorage.setItem('calendar_items', JSON.stringify(updated));
                                  return updated;
                                });
                              }
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              }
            } catch (error) {
              console.error('Error parsing message:', error);
            }

            // Render regular chat messages
            return (
              <div
                key={idx}
                className={`flex ${m.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-xl rounded-2xl px-4 py-3 text-sm shadow-sm transition-all duration-300 ${
                  m.role === 'assistant' 
                    ? 'bg-background border border-brand-green-border text-left' 
                    : 'bg-background text-foreground border border-transparent text-right'
                }`}>
                  {m.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground">
                      {m._loading ? (
                        <div className="flex items-center space-x-2">
                          <div className="relative w-6 h-6">
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-4 h-4 text-brand-green-border animate-grow-leaf">
                                🌱
                              </div>
                            </div>
                          </div>
                          <span className="text-muted-foreground text-sm">
                            {m._mode === 'idea' ? 'Generating ideas...' : 
                             m._mode === 'write' ? 'Creating outline...' : 
                             ''}
                          </span>
                        </div>
                      ) : m._streaming && m._typing ? (
                        <div className="flex items-center space-x-2">
                          <span>{m.content}</span>
                          <div className="w-2 h-2 bg-brand-green-border rounded-full animate-pulse"></div>
                        </div>
                      ) : (
                        <ReactMarkdown 
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="text-sm">{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                            a: ({ href, children }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">{children}</a>
                            )
                          }}
                        >
                          {m.content}
                        </ReactMarkdown>
                      )}
                    </div>
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Chat input */}
      <div className="p-4 border-t bg-background/50">
        {/* Image preview */}
        {selectedImage && (
          <div className="mb-3 flex items-start gap-3">
            <ChatImageUpload
              onImageSelect={handleImageSelect}
              onImageRemove={handleImageRemove}
              selectedImage={selectedImage}
              disabled={sending}
            />
            <div className="flex-1 text-sm text-muted-foreground">
              <p className="font-medium">{selectedImage.file.name}</p>
              <p className="text-xs">{(selectedImage.file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2 rounded-full border border-brand-green-border bg-muted/30 px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-brand-green-border/50">
          {!selectedImage && (
            <ChatImageUpload
              onImageSelect={handleImageSelect}
              onImageRemove={handleImageRemove}
              selectedImage={selectedImage}
              disabled={sending}
            />
          )}

          <Input
            ref={inputRef}
            placeholder={selectedImage ? "Describe what you'd like to do with this image..." : "What's on your mind?"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { 
              if (e.key === 'Enter' && !e.shiftKey) { 
                e.preventDefault(); 
                onSend(); 
              } 
            }}
            className="flex-1 bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-base"
            disabled={sending}
          />

          <button
            type="button"
            className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted transition-colors"
            aria-label="Voice"
          >
            <Mic className="h-4 w-4 text-muted-foreground" />
          </button>

          <Button
            onClick={onSend}
            disabled={sending || (!input.trim() && !selectedImage)}
            className="h-9 w-9 rounded-full p-0 shadow-sm"
            title="Send"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>


      {/* Write Post Drawer */}
      <WritePostDrawer
        isOpen={writeDrawerOpen}
        onClose={() => {
          setWriteDrawerOpen(false);
          setWriteDrawerData(null);
        }}
        onBack={() => {
          setWriteDrawerOpen(false);
          setDrawerOpen(true); // Go back to calendar drawer
        }}
        subject_matter={writeDrawerData?.subject_matter || ''}
        post_content={writeDrawerData?.post_content || ''}
        post_digest={writeDrawerData?.post_digest}
        onArticleGenerated={async (article) => {
          // Store the generated content for persistence
          console.log('Article generated:', article);
          setWriteDrawerData(prev => prev ? { ...prev, post_content: article.post_content } : null);
          
          // Check if we're editing an existing item (has an ID in writeDrawerData)
          if (writeDrawerData?.id) {
            // Update existing item
            setCalendarItems((prev) => {
              const updated = prev.map(item => 
                item.id === writeDrawerData.id 
                  ? { 
                      ...item, 
                      title: article.subject_matter,
                      digest: generateDigest(article.post_content),
                      fullContent: article.post_content
                    }
                  : item
              );
              localStorage.setItem('calendar_items', JSON.stringify(updated));
              console.log('Updated existing article:', updated);
              return updated;
            });
          } else {
            // Create new item and insert into database
            const itemId = crypto.randomUUID();
            
            // Insert into post_themes database table for Planned Content
            let postThemeId: string | undefined;
            if (currentWebsite?.id) {
              try {
                // Ensure user has website access before proceeding
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                  // Check if user has website access
                  const { data: websiteAccess, error: accessError } = await supabase
                    .from('website_access')
                    .select('id')
                    .eq('user_id', user.id)
                    .eq('website_id', currentWebsite.id)
                    .single();

                  if (accessError && accessError.code === 'PGRST116') {
                    // No access record found, create one
                    console.log('WritePostDrawer: Creating website access record for user');
                    const { error: createAccessError } = await supabase
                      .from('website_access')
                      .insert({
                        user_id: user.id,
                        website_id: currentWebsite.id,
                        access_status: 'confirmed'
                      });

                    if (createAccessError) {
                      console.error('WritePostDrawer: Failed to create website access:', createAccessError);
                    } else {
                      console.log('WritePostDrawer: Website access record created successfully');
                    }
                  } else if (accessError) {
                    console.error('WritePostDrawer: Error checking website access:', accessError);
                  } else {
                    console.log('WritePostDrawer: User has website access');
                  }
                }

                const { data: postTheme, error: postThemeError } = await supabase
                  .from('post_themes')
                  .insert({
                    website_id: currentWebsite.id,
                    subject_matter: article.subject_matter,
                    keywords: [], // Default empty keywords
                    status: 'generated',
                    post_content: article.post_content,
                    post_digest: generateDigest(article.post_content),
                    content_type: 'post',
                    format_template: 'guide',
                    length_tag: 'long',
                    tone_style: {},
                    section_layout: [],
                    element_settings: {}
                  })
                  .select()
                  .single();

                if (postThemeError) {
                  console.error('Error creating post theme:', postThemeError);
                } else {
                  console.log('Successfully created post theme:', postTheme);
                  postThemeId = postTheme.id;
                }
              } catch (error) {
                console.error('Error inserting post theme:', error);
              }
            }

            const item: CalendarItem = { 
              id: itemId, 
              title: article.subject_matter, 
              digest: generateDigest(article.post_content),
              fullContent: article.post_content,
              isGenerated: true,
              postThemeId,
              format_template: 'guide',
              length_tag: 'long',
              content_type: 'post',
              keywords: [],
              tone_style: {},
              section_layout: [],
              element_settings: {},
              scheduled_at: new Date().toISOString()
            };
            setCalendarItems((prev) => {
              const next = [item, ...prev];
              localStorage.setItem('calendar_items', JSON.stringify(next));
              localStorage.setItem('calendar_enabled', '1');
              window.dispatchEvent(new Event('calendar-enabled-changed'));
              console.log('Added generated article to calendar:', next);
              return next;
            });
          }
        }}
      />
    </div>
  );
}
